"use strict"

import _ from "lodash"
import { organizationManagerInstance as API } from "@/plugins/hylable-sdk"

import * as utils from "@/utils"

// 座席のメンバーは id と name、コースのメンバーは id のみを保存・送信する
const slimMember = (m) => m ? {id: m.id, name: m.name} : m
const slimGroupItem = (g) => Array.isArray(g?.seats) ? {...g, seats: g.seats.map((s) => ({...s, member: slimMember(s.member)}))} : g
const slimGroup = (group) => group ? Object.fromEntries(Object.entries(group).map(([k, g]) => [k, slimGroupItem(g)])) : group

const slimCourse = (course) => ({
  ...course,
  members: (course.members || []).map((m) => ({id: m.id})),
  group: slimGroup(course.group),
})

export default {
  async listOrganizations(){
    return API.get("/organization")
  },
  async createOrganization({input}){
    return API.post("/organization", input)
  },
  async getOrganization({oid}){
    return API.get(`/${oid}`)
  },
  async updateConfig({oid, input}){
    return API.post(`/${oid}/config`, input)
  },

  async listMembers({oid}){
    const res = await API.get(`/${oid}/member`)
    const members = _.map(res, (r) => new Member(r))
    return _.sortBy(members, "name")
  },
  async addMember({oid, input}){
    return API.post(`/${oid}/member`, input)
  },
  async getMember({oid, mid}){
    return API.get(`/${oid}/member/${mid}`)
  },
  async updateMember({oid, mid, input}){
    return API.put(`/${oid}/member/${mid}`, input)
  },
  async delMember({oid, mid}){
    return API.del(`/${oid}/member/${mid}`)
  },
  async createMemberAccount({oid, mid, input}){
    return API.post(`/${oid}/member/${mid}/account`, input)
  },
  async changeMemberAccount({oid, mid, input}){
    return API.put(`/${oid}/member/${mid}/account`, input)
  },
  async deleteMemberAccount({oid, mid}){
    return API.del(`/${oid}/member/${mid}/account`)
  },
  async changeMemberPassword({oid, mid, input}){
    return API.put(`/${oid}/member/${mid}/password`, input)
  },
  async setMemberPermission({oid, mid, input}){
    return API.put(`/${oid}/member/${mid}/permission`, input)
  },

  async listCourses({oid}){
    const res = await API.get(`/${oid}/course`)
    const courses = _.map(res, (c) => new Course(c))
    return _.sortBy(courses, "name")
  },
  async createCourse({oid, input}){
    const c = new Course()
    Object.assign(c, input)
    return API.post(`/${oid}/course`, c.toInput())
  },
  async getCourse({oid, cid}){
    const res = await API.get(`/${oid}/course/${cid}`)
    return new Course(res)
  },
  async updateCourse({oid, cid, input}){
    return API.put(`/${oid}/course/${cid}`, input)
  },
  async deleteCourse({oid, cid}){
    return API.del(`/${oid}/course/${cid}`)
  },
  async setCourseMember({oid, cid, input}){
    return API.put(`/${oid}/course/${cid}/member`, input)
  },
  async setCourseGroup({oid, cid, gid, input}){
    return API.put(`/${oid}/course/${cid}/group/${gid}`, slimGroupItem(input))
  },

  async updateCourseByDiff({oid, cid, before, after}){
    await Promise.all([
      (async () => {
        const diff = utils.changed(before, after)
        const {name, year, comment, lock, flags} = diff
        if(name || year || comment || lock || flags !== undefined) {
          await this.updateCourse({oid, cid, input: {name, year, comment, lock, flags}})
          .catch(() => {
            throw "course-error"
          })
        }
      })(),
      (async () => {
        const b = _.map(before.members, "id")
        const a = _.map(after.members, "id")
        const diff = _.difference(a, b)
        if(diff.length || b.length != a.length){
          await this.setCourseMember({oid, cid, input: a})
          .catch(() => {
            throw "member-error"
          })
        }
      })(),
      (async () => {
        const b = _.get(before.group, "preset.seats")
        const a = _.get(after.group, "preset.seats")
        const diff = _.difference(a, b)
        if(_.size(diff) || _.size(b) != _.size(a)){
          await this.setCourseGroup({oid, cid, gid: "preset", input: {seats: a}})
          .catch(() => {
            throw "seat-error"
          })
        }
      })(),
    ])
    return slimCourse(after)
  },
}

class Member {
  constructor(data){
    data = data || {}
    this.id = data.ID
    this.name = data.name
    this.account = data.account
    this.permission = data.permission
    this.idProvider = data.idProvider
  }
}

class Course {
  constructor(data){
    data = data || {}
    this.oid = data.OrgID
    this.id = data.ID
    this.name = data.name
    this.year = data.year
    this.comment = data.comment || ""
    this.group = slimGroup(data.group)
    this.flags = data.flags || {}
    // サーバーが返す member[] はメンバー全属性のコピーのため、ID だけ取り込む
    this.members = (data.member || []).map((r) => ({id: r.ID}))
    this.lock = data.lock || false
  }

  toInput(){
    return {
      OrgID: this.oid,
      ID: this.id,
      name: this.name,
      year: this.year,
      comment: this.comment,
      group: slimGroup(this.group),
      flags: this.flags,
      member: (this.members || []).map((m) => ({ID: m.id})),
    }
  }
}
